import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Marketplace, MarketplacePending, PendingRepoDrafts, PluginCatalog, PluginDetail, AgentPluginView } from "@/types";

function useClient() {
  const { session, logout } = useAuth();
  return () => createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
}

// #232 (UC-4): Marketplaces (Liste).
export function useMarketplaces(): UseQueryResult<Marketplace[]> {
  const { session } = useAuth();
  const getClient = useClient();
  return useQuery({
    queryKey: queryKeys.list("marketplaces"),
    queryFn: () => getClient().get<Marketplace[]>("/marketplaces"),
    enabled: !!session,
  });
}

// #232 (UC-4): einzelner Marketplace (Detail-Sicht).
export function useMarketplace(id: string | undefined): UseQueryResult<Marketplace> {
  const { session } = useAuth();
  const getClient = useClient();
  return useQuery({
    queryKey: id ? queryKeys.detail("marketplaces", id) : ["resources", "marketplaces", "_invalid"],
    queryFn: () => getClient().get<Marketplace>(`/marketplaces/${id}`),
    enabled: !!session && !!id,
  });
}

// #287: Plugins EINER Quelle — LIVE aus der marketplace.json gelesen. Die Antwort trägt
// `sources` mit: ist `ok:false`, war die Quelle nicht lesbar (Ladefehler ≠ leere Liste).
export function useMarketplacePlugins(id: string | undefined): UseQueryResult<PluginCatalog> {
  const { session } = useAuth();
  const getClient = useClient();
  return useQuery({
    queryKey: id ? queryKeys.subResource("marketplaces", id, "plugins") : ["resources", "marketplaces", "_invalid", "plugins"],
    queryFn: () => getClient().get<PluginCatalog>(`/marketplaces/${id}/plugins`),
    enabled: !!session && !!id,
  });
}

// #290 (UC-3): Plugin-Detail — EIN Ort für ein Plugin (Meta + Entscheidungen + genutzt-von
// + Repo-Zusage). `repoId` kommt vom Server; die UI rät nie aus Adressen.
export function usePluginDetail(marketplaceId: string | undefined, pluginName: string | undefined): UseQueryResult<PluginDetail> {
  const { session } = useAuth();
  const getClient = useClient();
  return useQuery({
    queryKey: ["resources", "plugins", "detail", marketplaceId ?? "_none", pluginName ?? "_none"],
    queryFn: () =>
      getClient().get<PluginDetail>(
        `/plugins/${encodeURIComponent(marketplaceId ?? "")}/${encodeURIComponent(pluginName ?? "")}`,
      ),
    enabled: !!session && !!marketplaceId && !!pluginName,
  });
}

// #287: Plugin-Katalog über alle Quellen (live gelesen, kein Spiegel).
export function usePlugins(): UseQueryResult<PluginCatalog> {
  const { session } = useAuth();
  const getClient = useClient();
  return useQuery({
    queryKey: queryKeys.list("plugins"),
    queryFn: () => getClient().get<PluginCatalog>("/plugins"),
    enabled: !!session,
  });
}

// #232 (UC-2/3/4): Katalog-Plugins + Enablement-Status + Drift für EINEN Agent (Per-Agent-Section).
export function useAgentPlugins(agentId: string | undefined): UseQueryResult<AgentPluginView> {
  const { session } = useAuth();
  const getClient = useClient();
  return useQuery({
    queryKey: agentId ? queryKeys.subResource("agents", agentId, "plugins") : ["resources", "agents", "_invalid", "plugins"],
    queryFn: () => getClient().get<AgentPluginView>(`/agents/${agentId}/plugins`),
    enabled: !!session && !!agentId,
  });
}

// #291 (UC-1): Was wartet an DIESEM Katalog auf Freigabe — Entwürfe + Plugins „in Aufnahme"
// (Diff Entwurfs-Manifest minus live). Kein Poll (die Reads kosten Gitea-Roundtrips) —
// Aktualität kommt aus der Lifecycle-Invalidierung nach jeder Aktion + staleTime-Standard.
export function useMarketplacePending(id: string | undefined): UseQueryResult<MarketplacePending> {
  const { session } = useAuth();
  const getClient = useClient();
  return useQuery({
    queryKey: id ? queryKeys.subResource("marketplaces", id, "pending") : ["resources", "marketplaces", "_invalid", "pending"],
    queryFn: () => getClient().get<MarketplacePending>(`/marketplaces/${id}/pending`),
    enabled: !!session && !!id,
  });
}

// #291 (UC-3): die globale Freigabe-Arbeitsliste (Banner/Zähler auf der Plugins-Liste).
export function usePendingDrafts(): UseQueryResult<PendingRepoDrafts[]> {
  const { session } = useAuth();
  const getClient = useClient();
  return useQuery({
    queryKey: ["resources", "git-content", "pending-drafts"],
    // ?kind=marketplace: der einzige Konsument (Plugins-Liste) wirft tpl/plg eh weg —
    // serverseitig zugeschnitten spart das die teuersten Gitea-Roundtrips (Review-Fund 2).
    // ⚠ Kommt je ein Konsument OHNE kind dazu, muss kind in den Query-Key (sonst
    // teilen sich beide denselben Cache-Eintrag mit verschiedenem Inhalt).
    queryFn: () => getClient().get<PendingRepoDrafts[]>("/git-content/pending-drafts?kind=marketplace"),
    enabled: !!session,
  });
}
